import { toZonedTime } from 'date-fns-tz'
import { TFunction } from 'i18next'
import { z } from 'zod'

import { SurveyResponder } from '@/api/communication/surveys/get-survey-responders'
import { getPersonColumn, PersonColumnSettings } from '@/components/business/person-column'
import { StatusTabOption } from '@/components/ui/status-tabs'
import { TableColumn } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants'
import { getAssetLink } from '@/helpers/get-asset-link'
import { SurveyQuestion } from '@/models'
import {
  getRecipientStatusColor,
  SurveyCommunicationType,
  SurveyCommunicationTypeEnum,
  SurveyResponderStatus,
  SurveyResponderStatusEnum,
} from '@/models/features'
import { formatDateOnly, formatTimeOnly } from '@/utils/date'

import * as S from './survey-recipients-table.styles'

export const getSurveyResponderStatusLabel = (
  status: SurveyResponderStatus,
  t: TFunction<['communication']>,
): string => {
  const STATUS_LABEL_MAP: Record<SurveyResponderStatus, string> = {
    delivered: t('communication:surveyDetails.recipientsTable.status.delivered'),
    opened: t('communication:surveyDetails.recipientsTable.status.opened'),
    replied: t('communication:surveyDetails.recipientsTable.status.replied'),
    sent: t('communication:surveyDetails.recipientsTable.status.sent'),
  }

  return STATUS_LABEL_MAP[status]
}

export const surveyRecipientsTableFiltersSchema = z.object({
  limit: z.coerce.number().optional(),
  search: z.string().optional(),
  skip: z.coerce.number().optional(),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  status: SurveyResponderStatusEnum.optional(),
})

export type SurveyRecipientsTableFilters = z.infer<typeof surveyRecipientsTableFiltersSchema>

export const SURVEY_RECIPIENTS_TABLE_INITIAL_STATE: SurveyRecipientsTableFilters = {
  search: '',
  skip: 0,
  sortBy: undefined,
  sortOrder: undefined,
  status: undefined,
}

// TODO: Simplify this entire file
export const EMAIL_STATUSES: SurveyResponderStatus[] = [
  SurveyResponderStatusEnum.enum.sent,
  SurveyResponderStatusEnum.enum.delivered,
  SurveyResponderStatusEnum.enum.opened,
  SurveyResponderStatusEnum.enum.replied,
]

export const SMS_STATUSES: SurveyResponderStatus[] = [
  SurveyResponderStatusEnum.enum.delivered,
  SurveyResponderStatusEnum.enum.replied,
]

export const IN_APP_STATUSES: SurveyResponderStatus[] = [
  SurveyResponderStatusEnum.enum.sent,
  SurveyResponderStatusEnum.enum.delivered,
  SurveyResponderStatusEnum.enum.replied,
]

export const getSurveyCommunicationTypeStatusTabs = (
  t: TFunction<['communication']>,
): Record<SurveyCommunicationType, StatusTabOption<string>[]> => {
  const allTab: StatusTabOption<string> = {
    label: t('communication:surveyDetails.recipientsTable.status.all'),
    value: '',
  }

  const toTabs = (statuses: SurveyResponderStatus[]) => [
    allTab,
    ...statuses.map((status) => ({
      color: getRecipientStatusColor(status),
      label: getSurveyResponderStatusLabel(status, t),
      value: status,
    })),
  ]

  return {
    [SurveyCommunicationTypeEnum.enum.email]: toTabs(EMAIL_STATUSES),
    [SurveyCommunicationTypeEnum.enum.sms]: toTabs(SMS_STATUSES),
    [SurveyCommunicationTypeEnum.enum['in-app']]: toTabs(IN_APP_STATUSES),
  }
}

export const getSurveyTableColumns = (
  t: TFunction<['communication']>,
  questions: SurveyQuestion[],
  isInAppCommunication: boolean,
  timezone: string,
  residentColumnSettings: PersonColumnSettings,
): TableColumn<SurveyResponder>[] => {
  const baseColumns: TableColumn<SurveyResponder>[] = [
    getPersonColumn(residentColumnSettings, {
      getDisplay: (row) => row.residentDisplay,
      getPerson: ({ user }) => ({ ...user, kind: 'resident' }),
      id: 'resident',
      pageName: t('communication:surveyDetails.recipientsTable.pageName'),
      source: 'surveys',
      title: t('communication:surveyDetails.recipientsTable.columns.resident'),
    }),
    {
      id: 'status',
      render: (responder) => {
        if (!responder.status) return EMPTY_VALUE
        return (
          <S.CapitalizedBadge
            color={getRecipientStatusColor(responder.status) || 'neutral'}
            size="small"
            variant="light"
          >
            {responder.status}
          </S.CapitalizedBadge>
        )
      },
      title: t('communication:surveyDetails.recipientsTable.columns.status'),
      width: '12rem',
    },
  ]

  const hasMultipleQuestions = isInAppCommunication && questions.length > 1

  let questionColumns: TableColumn<SurveyResponder>[]
  if (hasMultipleQuestions) {
    questionColumns = questions.map((question, index) => ({
      id: `question-${question.order}`,
      render: (responder) => (
        <Typography as="div" color="black800" size="text-small" breakLines={1}>
          {responder.answerDetails?.[index]?.answer || EMPTY_VALUE}
        </Typography>
      ),
      title: t('communication:surveyDetails.recipientsTable.columns.questionReply', {
        order: question.order,
      }),
      width: '15rem',
    }))
  } else {
    questionColumns = [
      {
        id: 'reply',
        render: (responder) => (
          <Typography as="div" color="black800" size="text-small" breakLines={1}>
            {responder.answerDetails?.[0]?.answer || EMPTY_VALUE}
          </Typography>
        ),
        title: t('communication:surveyDetails.recipientsTable.columns.reply'),
        width: '15rem',
      },
    ]
  }

  // Note: Comment column (only for in-app)
  let commentColumn: TableColumn<SurveyResponder>[]
  if (isInAppCommunication) {
    commentColumn = [
      {
        id: 'comment',
        render: (responder) => (
          <Typography as="div" color="black800" size="text-small" breakLines={1}>
            {responder.comment || EMPTY_VALUE}
          </Typography>
        ),
        title: t('communication:surveyDetails.recipientsTable.columns.comment'),
        width: '20rem',
      },
    ]
  } else {
    commentColumn = []
  }

  // Note: Last Updated column
  const lastUpdatedColumn: TableColumn<SurveyResponder>[] = [
    {
      id: 'lastUpdated',
      render: (responder) => {
        if (!responder.updatedAt) return <Typography size="text-small">-</Typography>

        const zonedDate = toZonedTime(responder.updatedAt, timezone)
        return (
          <S.DateTimeCell>
            <Typography as="span" color="black600" size="text-small" weight="medium">
              {formatDateOnly(zonedDate)}
            </Typography>
            <Typography as="span" color="black600" size="text-small">
              {formatTimeOnly(zonedDate)}
            </Typography>
          </S.DateTimeCell>
        )
      },
      sortAscLabel: t('communication:surveyDetails.recipientsTable.sort.oldestFirst'),
      sortDescLabel: t('communication:surveyDetails.recipientsTable.sort.newestFirst'),
      sortKey: 'updatedAt',
      title: t('communication:surveyDetails.recipientsTable.columns.lastUpdated'),
      width: '15rem',
    },
  ]

  return [...baseColumns, ...questionColumns, ...commentColumn, ...lastUpdatedColumn]
}
